import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";

/* HelpDesk → Mail setup
 *
 * Makes inbound support email work. This appliance IS the mail server for its
 * own subdomain — there is no mailbox to sign into, no relay, and no hosted
 * provider — so the wizard is a checklist of the four things that have to be
 * true, in the order they have to be done:
 *
 *   1. Address   what this deployment's support address is (derived, not typed)
 *   2. DNS       one MX record, shown exactly as it must be entered
 *   3. Port 25   forwarded to this appliance
 *   4. Receiving turn the listener on
 *   5. Filtering what to drop before it becomes a ticket
 *
 * Every step reports what is ACTUALLY true rather than what was configured.
 * Deriving an address, DNS pointing at it, and mail arriving are three separate
 * claims; conflating them is how inbound ticketing looked configured for months
 * while silently receiving nothing.
 */

type DnsInfo = {
  domain: string | null;
  helpdesk_address: string | null;
  expected_mx: string | null;
  mx: string[];
  resolved_ip: string[] | null;
  public_ip: string | null;
  mx_ok: boolean;
  a_ok: boolean;
  ip_ok: boolean;
  problems: string[];
};

type Identity = {
  slug: string | null;
  mail_domain: string | null;
  helpdesk_address: string | null;
  overridden: boolean;
  blockers: string[];
  alerts_address?: string | null;
  noreply_address?: string | null;
  sending_enabled?: boolean;
  receiver_enabled?: boolean;
  receiver_listening?: boolean;
  receiver_port?: number;
  helpdesk_notify_email?: string | null;
  helpdesk_notify_email_explicit?: boolean;
  dns?: DnsInfo;
  mx?: string[];
  sending_direct?: boolean;
  outbound_dns?: OutboundDns;
  outbound_queue?: { queued: number; failed: number; last_error: string | null } | null;
};

/** What DNS says about our ability to SEND. Distinct from inbound: receiving
 *  needs an MX, sending needs SPF, DKIM and ideally a matching PTR. */
type OutboundDns = {
  domain: string | null;
  public_ip: string | null;
  spf: string | null;
  spf_ok: boolean;
  dkim_selector: string;
  dkim_published: boolean;
  dkim_expected: string | null;
  ptr: string[] | null;
  ptr_ok: boolean;
  problems: string[];
};

type BlockRule = {
  id: number;
  pattern: string;
  reason: string | null;
  active: boolean;
  hit_count: number;
  match_type: "exact" | "domain";
};

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "mt-1 w-full px-2 py-1.5 border rounded text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100";
const BTN_PRIMARY =
  "px-3 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50";
const BTN_PLAIN =
  "px-3 py-1.5 rounded text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 disabled:opacity-50";

function StepHeader({
  n,
  title,
  state,
  children,
}: {
  n: number;
  title: string;
  state: "ok" | "warn" | "todo";
  children?: React.ReactNode;
}) {
  const ring =
    state === "ok"
      ? "bg-green-600 text-white"
      : state === "warn"
        ? "bg-amber-500 text-white"
        : "bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200";
  return (
    <div className="flex items-start gap-3">
      <span
        className={`shrink-0 w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${ring}`}
      >
        {state === "ok" ? "✓" : n}
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold dark:text-gray-100">{title}</h2>
        {children && <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{children}</p>}
      </div>
    </div>
  );
}

/** A DNS record rendered as the fields an operator actually types, with copy
 *  buttons. Most DNS UIs (GoDaddy included) want the LABEL in the host field,
 *  not the full name — getting that wrong creates acme.aerogauge.net.aerogauge.net,
 *  which is the single most common way this step fails. */
function DnsRow({
  type,
  host,
  value,
  priority,
}: {
  type: string;
  host: string;
  value: string;
  priority?: string;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (what: string, text: string) => {
    navigator.clipboard?.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 1200);
  };
  const Cell = ({ label, text }: { label: string; text: string }) => (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <button
        onClick={() => copy(label, text)}
        title="Copy"
        className="font-mono text-xs text-left break-all hover:underline dark:text-gray-100"
      >
        {text}
        {copied === label && <span className="ml-1 text-green-600">copied</span>}
      </button>
    </div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40">
      <Cell label="Type" text={type} />
      <Cell label="Host / Name" text={host} />
      <Cell label="Points to / Value" text={value} />
      {priority ? <Cell label="Priority" text={priority} /> : <div />}
    </div>
  );
}

export default function HelpDeskSetup() {
  const [ident, setIdent] = useState<Identity | null>(null);
  const [rules, setRules] = useState<BlockRule[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [dnsBusy, setDnsBusy] = useState(false);
  const [rxBusy, setRxBusy] = useState(false);
  const [rxDetail, setRxDetail] = useState<string | null>(null);

  const [override, setOverride] = useState("");
  const [ovBusy, setOvBusy] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  const [newRule, setNewRule] = useState({ pattern: "", match_type: "exact" as const, reason: "" });
  const [ruleBusy, setRuleBusy] = useState(false);

  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifySaved, setNotifySaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, r] = await Promise.all([
        api.get<Identity>("/api/v1/email-intake/identity"),
        api.get<BlockRule[]>("/api/v1/email-intake/block-rules"),
      ]);
      setIdent(i.data);
      setRules(r.data);
      // Only prefill when explicitly set, so the inherited fallback shows as a
      // placeholder rather than looking like a saved value.
      setNotifyEmail(
        i.data.helpdesk_notify_email_explicit ? i.data.helpdesk_notify_email || "" : "",
      );
      setLoadErr(null);
    } catch (e: any) {
      setLoadErr(e?.response?.data?.detail || e?.message || "Could not load setup state");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recheckDns = async () => {
    setDnsBusy(true);
    try {
      const r = await api.get<DnsInfo>("/api/v1/email-intake/dns-check");
      setIdent((p) => (p ? { ...p, dns: r.data, mx: r.data.mx } : p));
    } finally {
      setDnsBusy(false);
    }
  };

  const toggleReceiver = async (enabled: boolean) => {
    setRxBusy(true);
    setRxDetail(null);
    try {
      const r = await api.post("/api/v1/email-intake/receiver", { enabled });
      setRxDetail(r.data.detail || null);
      await load();
    } catch (e: any) {
      setRxDetail(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setRxBusy(false);
    }
  };

  const saveOverride = async () => {
    setOvBusy(true);
    try {
      await api.put("/api/v1/settings/helpdesk_address_override", { value: override.trim() });
      setShowOverride(false);
      await load();
    } finally {
      setOvBusy(false);
    }
  };

  const saveNotifyEmail = async () => {
    setNotifyBusy(true);
    setNotifySaved(false);
    try {
      await api.put("/api/v1/settings/helpdesk_notify_email", { value: notifyEmail.trim() });
      setNotifySaved(true);
      await load();
      setTimeout(() => setNotifySaved(false), 2500);
    } finally {
      setNotifyBusy(false);
    }
  };

  const addRule = async () => {
    if (!newRule.pattern.trim()) return;
    setRuleBusy(true);
    try {
      await api.post("/api/v1/email-intake/block-rules", {
        pattern: newRule.pattern.trim(),
        match_type: newRule.match_type,
        reason: newRule.reason.trim() || null,
      });
      setNewRule({ pattern: "", match_type: "exact", reason: "" });
      await load();
    } finally {
      setRuleBusy(false);
    }
  };

  const removeRule = async (id: number) => {
    setRuleBusy(true);
    try {
      await api.delete(`/api/v1/email-intake/block-rules/${id}`);
      await load();
    } finally {
      setRuleBusy(false);
    }
  };

  const dns = ident?.dns;
  const odns = ident?.outbound_dns;
  const addrOk = !!ident?.helpdesk_address;
  const dnsOk = !!dns?.mx_ok && !!dns?.ip_ok;
  const rxOn = !!ident?.receiver_enabled;
  const rxUp = !!ident?.receiver_listening;
  const allDone = addrOk && dnsOk && rxOn && rxUp;

  const domain = ident?.mail_domain || "";
  const label = ident?.slug || "";

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold dark:text-gray-100">HelpDesk setup</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Lets customers open and reply to tickets by email. This appliance receives the mail
          itself &mdash; there is no mailbox to create, no password to store, and nothing runs
          through Microsoft&nbsp;365, Google or AeroGauge.
        </p>
      </div>

      {loadErr && (
        <div className="rounded border border-red-200 bg-red-50 dark:bg-red-900/30 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          {loadErr}
        </div>
      )}

      {allDone && (
        <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-900/30 dark:border-green-800 p-3 text-sm text-green-800 dark:text-green-300">
          <strong>Email is live.</strong> Mail to{" "}
          <span className="font-mono">{ident?.helpdesk_address}</span> becomes a ticket, and a
          customer replying to a ticket notification adds a comment to it. Watch messages arrive
          on <Link to="/helpdesk/email-intake" className="underline">Email intake</Link>.
        </div>
      )}

      {/* ── 1. Address ─────────────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader n={1} title="Your support address" state={addrOk ? "ok" : "todo"}>
          Derived from the business name you entered at setup &mdash; the same name that gives this
          appliance its web address. Nothing to type.
        </StepHeader>

        {addrOk ? (
          <div className="pl-9 space-y-2">
            <div className="text-lg font-mono dark:text-gray-100">{ident?.helpdesk_address}</div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Give this address to customers. Ticket notifications already use it as the reply
              address, so a customer just hits Reply.
            </p>
            {!showOverride ? (
              <button onClick={() => setShowOverride(true)} className="text-xs text-blue-600 hover:underline">
                Use an address on my own domain instead
              </button>
            ) : (
              <div className="rounded border border-gray-200 dark:border-gray-600 p-3 space-y-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Only if you would rather customers saw your own domain. You then have to forward
                  that address into this appliance yourself, and the DNS below applies to your
                  domain rather than this one.
                </p>
                <input
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  placeholder="helpdesk@yourdomain.com"
                  className={INPUT}
                />
                <div className="flex gap-2">
                  <button onClick={saveOverride} disabled={ovBusy} className={BTN_PRIMARY}>
                    {ovBusy ? "Saving…" : "Save"}
                  </button>
                  <button onClick={() => setShowOverride(false)} className={BTN_PLAIN}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="pl-9 text-sm text-amber-700 dark:text-amber-400">
            No business name is recorded, so no address can be derived. Set it in Setup first.
          </div>
        )}
      </div>

      {/* ── 2. DNS ─────────────────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader n={2} title="Add one DNS record" state={dnsOk ? "ok" : dns?.a_ok ? "warn" : "todo"}>
          Tells the internet to deliver mail for <span className="font-mono">{domain}</span> to this
          appliance. Add it wherever <span className="font-mono">aerogauge.net</span> DNS is hosted.
        </StepHeader>

        <div className="pl-9 space-y-3">
          <DnsRow type="MX" host={label} value={domain} priority="10" />
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc pl-4">
            <li>
              Put <span className="font-mono">{label}</span> in the Host field, <strong>not</strong>{" "}
              <span className="font-mono">{domain}</span> &mdash; most DNS panels add the domain for
              you, and typing the full name creates{" "}
              <span className="font-mono">{domain}.aerogauge.net</span>.
            </li>
            <li>
              This does not touch any mail you already have. An MX on{" "}
              <span className="font-mono">aerogauge.net</span> itself is a different record and
              stays exactly as it is.
            </li>
            <li>No A record is needed &mdash; this appliance already has one.</li>
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={recheckDns} disabled={dnsBusy} className={BTN_PLAIN}>
              {dnsBusy ? "Checking…" : "Check DNS now"}
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Changes can take a few minutes to publish.
            </span>
          </div>

          {dns && (
            <div className="space-y-1 text-xs">
              <Check ok={dns.mx_ok}>
                {dns.mx_ok ? (
                  <>
                    MX for <span className="font-mono">{dns.domain}</span> points here
                  </>
                ) : dns.mx.length ? (
                  <>
                    MX points at <span className="font-mono">{dns.mx.join(", ")}</span>, not at this
                    appliance &mdash; mail is being delivered somewhere else
                  </>
                ) : (
                  <>
                    No MX record found for <span className="font-mono">{dns.domain}</span> yet
                  </>
                )}
              </Check>
              <Check ok={dns.ip_ok}>
                {dns.ip_ok ? (
                  <>
                    <span className="font-mono">{dns.domain}</span> resolves to this site&rsquo;s
                    address (<span className="font-mono">{dns.public_ip}</span>)
                  </>
                ) : (
                  <>
                    This site is at <span className="font-mono">{dns.public_ip || "unknown"}</span>{" "}
                    but <span className="font-mono">{dns.domain}</span> resolves to{" "}
                    <span className="font-mono">{(dns.resolved_ip || []).join(", ") || "nothing"}</span>{" "}
                    &mdash; update the A record or mail goes to the wrong place
                  </>
                )}
              </Check>
            </div>
          )}
        </div>
      </div>

      {/* ── 3. Port 25 ─────────────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader n={3} title="Forward port 25 to this appliance" state={dnsOk && rxUp ? "ok" : "todo"}>
          Mail servers deliver on TCP&nbsp;25. Your router already forwards 443 for the console;
          port 25 needs the same treatment.
        </StepHeader>
        <div className="pl-9 space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="p-3 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 font-mono text-xs dark:text-gray-100">
            TCP 25 &nbsp;→&nbsp; {dns?.public_ip || "this site"} &nbsp;→&nbsp; this appliance
          </div>
          <p>
            There is nothing to verify from in here &mdash; only an outside sender can prove it
            works. Once the record above is live and receiving is on, send a message to{" "}
            <span className="font-mono">{ident?.helpdesk_address}</span> from any outside mailbox
            and watch it land on{" "}
            <Link to="/helpdesk/email-intake" className="underline text-blue-600">
              Email intake
            </Link>
            .
          </p>
          <p>
            If nothing arrives, it is almost always this step. Some ISPs block inbound 25; if yours
            does, use an address on your own domain (step 1) and forward it here instead.
          </p>
        </div>
      </div>

      {/* ── 4. Receiving ───────────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader
          n={4}
          title="Turn on receiving"
          state={rxOn && rxUp ? "ok" : rxOn ? "warn" : "todo"}
        >
          Starts listening for mail. Off by default, because it opens a port to the internet.
        </StepHeader>
        <div className="pl-9 space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => toggleReceiver(!rxOn)}
              disabled={rxBusy}
              className={rxOn ? BTN_PLAIN : BTN_PRIMARY}
            >
              {rxBusy ? "Working…" : rxOn ? "Turn off" : "Turn on receiving"}
            </button>
            <span className="text-xs">
              {rxOn && rxUp && (
                <span className="text-green-700 dark:text-green-400">
                  Listening on port {ident?.receiver_port}
                </span>
              )}
              {rxOn && !rxUp && (
                <span className="text-amber-700 dark:text-amber-400">
                  On, but not bound to port {ident?.receiver_port}
                </span>
              )}
              {!rxOn && <span className="text-gray-500 dark:text-gray-400">Not receiving mail</span>}
            </span>
          </div>
          {rxDetail && (
            <div className="text-xs text-amber-700 dark:text-amber-400">{rxDetail}</div>
          )}
          <p className="text-xs text-gray-600 dark:text-gray-400">
            It accepts mail for{" "}
            <span className="font-mono">{ident?.helpdesk_address}</span> and nothing else, so it
            can never be used to relay someone else&rsquo;s mail.
          </p>
        </div>
      </div>

      {/* ── 5. Who gets told ───────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader
          n={5}
          title="Who gets told about tickets"
          state={ident?.helpdesk_notify_email ? "ok" : "todo"}
        >
          Your own address for ticket activity &mdash; separate from what customers see.
        </StepHeader>
        <div className="pl-9 space-y-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400">
              Notify this address when a ticket arrives
            </label>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder={ident?.helpdesk_notify_email || "you@yourcompany.com"}
                className={`${INPUT} sm:w-80 mt-0`}
              />
              <button onClick={saveNotifyEmail} disabled={notifyBusy} className={BTN_PRIMARY}>
                {notifyBusy ? "Saving…" : "Save"}
              </button>
              {notifySaved && (
                <span className="text-xs text-green-700 dark:text-green-400">Saved</span>
              )}
            </div>
            {!ident?.helpdesk_notify_email_explicit && ident?.helpdesk_notify_email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Currently inheriting{" "}
                <span className="font-mono">{ident.helpdesk_notify_email}</span> from the alert
                address. Set one here to use a different inbox.
              </p>
            )}
          </div>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-disc pl-4">
            <li>
              Gets a copy when a new ticket is opened, including tickets AeroGauge raises itself
              for a device or circuit going down.
            </li>
            <li>
              The technician a ticket is <strong>assigned to</strong> is always emailed about
              activity on it &mdash; no setting needed.
            </li>
            <li>
              This is not the address customers see. That one lives in Settings &rarr; Branding
              and is shown on the login and portal pages.
            </li>
          </ul>
        </div>
      </div>

      {/* ── 6. Filtering ───────────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader n={6} title="Block unwanted senders" state={rules.length ? "ok" : "todo"}>
          Optional. Blocked senders are logged and dropped without creating a ticket. Mail from an
          address that matches no customer is already dropped automatically.
        </StepHeader>
        <div className="pl-9 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <input
              value={newRule.pattern}
              onChange={(e) => setNewRule({ ...newRule, pattern: e.target.value })}
              placeholder="noreply@vendor.com or vendor.com"
              className={`${INPUT} sm:col-span-2`}
            />
            <select
              value={newRule.match_type}
              onChange={(e) => setNewRule({ ...newRule, match_type: e.target.value as any })}
              className={INPUT}
            >
              <option value="exact">exact address</option>
              <option value="domain">whole domain</option>
            </select>
            <button onClick={addRule} disabled={ruleBusy || !newRule.pattern.trim()} className={BTN_PRIMARY}>
              Add
            </button>
          </div>
          {rules.length > 0 && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {rules.map((r) => (
                <div key={r.id} className="py-2 flex items-center gap-3 text-sm">
                  <span className="font-mono dark:text-gray-100">{r.pattern}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{r.match_type}</span>
                  {r.hit_count > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {r.hit_count} blocked
                    </span>
                  )}
                  <button
                    onClick={() => removeRule(r.id)}
                    disabled={ruleBusy}
                    className="ml-auto text-xs text-red-600 hover:underline disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 7. Outgoing mail ───────────────────────────────────────── */}
      <div className={`${CARD} p-4 space-y-3`}>
        <StepHeader
          n={7}
          title="Outgoing mail"
          state={odns && odns.spf_ok && odns.dkim_published ? "ok" : "warn"}
        >
          This appliance sends its own mail too &mdash; there is no account to configure and
          nothing goes through Microsoft&nbsp;365 or Google.
        </StepHeader>

        <div className="pl-9 space-y-3">
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
            <div>
              Tickets from <span className="font-mono">{ident?.helpdesk_address || "—"}</span>
            </div>
            <div>
              Alerts from <span className="font-mono">{ident?.alerts_address || "—"}</span>
            </div>
            <div>
              Everything else from{" "}
              <span className="font-mono">{ident?.noreply_address || "—"}</span>
            </div>
            <div>
              Replies to all of it reach{" "}
              <span className="font-mono">{ident?.helpdesk_address || "—"}</span>
            </div>
          </div>

          {ident?.outbound_queue && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Queue: {ident.outbound_queue.queued} waiting
              {ident.outbound_queue.failed ? `, ${ident.outbound_queue.failed} gave up` : ""}.
              Messages are signed and queued instantly, then delivered within a minute.
              {ident.outbound_queue.last_error && (
                <> Last error: {ident.outbound_queue.last_error}</>
              )}
            </p>
          )}

          <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mt-2 mb-1">
              Two more DNS records, so your mail is trusted rather than treated as forged
            </p>
            {odns ? (
              <div className="space-y-2">
                {!odns.spf_ok && odns.public_ip && (
                  <DnsRow
                    type="TXT"
                    host={label}
                    value={`v=spf1 ip4:${odns.public_ip} -all`}
                  />
                )}
                {!odns.dkim_published && odns.dkim_expected && (
                  <DnsRow
                    type="TXT"
                    host={`${odns.dkim_selector}._domainkey.${label}`}
                    value={odns.dkim_expected}
                  />
                )}
                <div className="space-y-1 text-xs">
                  <Check ok={!!odns.spf_ok}>
                    {odns.spf_ok
                      ? "SPF authorises this appliance to send for the domain"
                      : "No SPF record yet — receivers cannot tell your mail from a forgery"}
                  </Check>
                  <Check ok={!!odns.dkim_published}>
                    {odns.dkim_published
                      ? "DKIM published and matches this appliance's signing key"
                      : "DKIM not published yet — mail is signed but the signature cannot be checked"}
                  </Check>
                  <Check ok={!!odns.ptr_ok}>
                    {odns.ptr_ok ? (
                      "Reverse DNS matches the mail domain"
                    ) : (
                      <>
                        Reverse DNS is{" "}
                        <span className="font-mono">{(odns.ptr || []).join(", ") || "not set"}</span>
                        , not <span className="font-mono">{odns.domain}</span>. Usually only whoever
                        provides your internet connection can change this. Mail will still be
                        accepted by most receivers; expect the occasional spam folder until it
                        matches.
                      </>
                    )}
                  </Check>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Check({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`flex items-start gap-1.5 ${
        ok ? "text-green-700 dark:text-green-400" : "text-amber-700 dark:text-amber-400"
      }`}
    >
      <span className="shrink-0">{ok ? "✓" : "•"}</span>
      <span>{children}</span>
    </div>
  );
}
